import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import * as listViewService from '../../../services/listViewService';

export interface UseDataTableViewsOptions {
  entityType: 'patients' | 'users' | 'privileges' | 'smsTemplates' | 'emailTemplates' | 'clinics' | 'meetingTypes' | 'summaryTypes' | 'prompts' | 'activityLogs' | 'questionnaires' | 'questionnaireTypes' | 'patientNotifications' | 'meetings' | 'medications' | 'wellness-configs' | 'wellness-items' | 'wellness-tips' | 'diagnosis-types' | 'tasks' | 'taskRules' | 'internalTasks' | 'internalTaskTypes' | 'referrals' | 'leads' | 'services';
  tenantId?: number | null;
}

export interface UseDataTableViewsReturn {
  savedViews: listViewService.ListView[];
  currentViewId: string | null;
  saveViewModalOpen: boolean;
  setSaveViewModalOpen: (open: boolean) => void;
  setCurrentViewId: (viewId: string | null) => void;
  handleSelectView: (viewId: string) => void;
  handleSaveView: () => void;
  handleSaveAsView: () => void;
  handleDeleteView: (viewId: string) => void;
  handleSetDefaultView: (viewId: string) => void;
  handleSaveViewModalSave: (name: string, isPublic: boolean) => Promise<void>;
  refreshViews: () => Promise<void>;
}

/**
 * Hook for managing table views (saved column configurations and sort settings).
 * Handles loading, saving, deleting, and applying views.
 *
 * @example
 * const viewsHook = useDataTableViews({
 *   entityType: 'patients',
 *   tenantId: currentTenant.id,
 * });
 *
 * // When loading a view, the page should:
 * // 1. Get view data from viewsHook.savedViews
 * // 2. Apply preferences and sort manually
 */
function useDataTableViews({
  entityType,
  tenantId = 0,
}: UseDataTableViewsOptions): UseDataTableViewsReturn {
  const [savedViews, setSavedViews] = useState<listViewService.ListView[]>([]);
  const [currentViewId, setCurrentViewId] = useState<string | null>(null);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);

  // Normalize tenantId: null becomes 0 for API calls
  const normalizedTenantId = tenantId ?? 0;

  // Track previous values to detect changes
  const prevEntityRef = useRef(entityType);
  const prevTenantRef = useRef(normalizedTenantId);
  const initialLoadDoneRef = useRef(false);

  // Load views on mount - only when entityType or tenantId changes
  useEffect(() => {
    console.log('[useDataTableViews] Effect triggered with:', {
      entityType,
      normalizedTenantId,
      prevEntity: prevEntityRef.current,
      prevTenant: prevTenantRef.current,
      initialLoadDone: initialLoadDoneRef.current,
      currentViewsCount: savedViews.length
    });

    // Detect actual changes to entity/tenant
    const entityChanged = prevEntityRef.current !== entityType;
    const tenantChanged = prevTenantRef.current !== normalizedTenantId;

    // Skip if already loaded and no parameter change
    if (initialLoadDoneRef.current && savedViews.length > 0 && !entityChanged && !tenantChanged) {
      console.log('[useDataTableViews] Skipping load - already loaded and no changes');
      return;
    }

    // Reset load flag on actual parameter change
    if (entityChanged || tenantChanged) {
      console.log('[useDataTableViews] Parameters changed, resetting load flag');
      initialLoadDoneRef.current = false;
      prevEntityRef.current = entityType;
      prevTenantRef.current = normalizedTenantId;
    }

    // Don't make API calls with an invalid tenant ID
    if (!normalizedTenantId) {
      console.log('[useDataTableViews] Skipping load - tenantId not yet available');
      return;
    }

    let cancelled = false;

    const loadViews = async () => {
      console.log('[useDataTableViews] Starting load for:', entityType, 'tenantId:', normalizedTenantId);
      try {
        const views = await listViewService.getListViews(entityType, normalizedTenantId);

        if (cancelled) {
          console.log('[useDataTableViews] Load cancelled, ignoring results');
          return;
        }

        console.log('[useDataTableViews] Loaded views:', views);
        setSavedViews(views);
        initialLoadDoneRef.current = true;

        // Check for latest chosen view first
        const latestChosenViewId = listViewService.getLatestChosenView(String(normalizedTenantId));
        console.log('[useDataTableViews] Latest chosen view ID:', latestChosenViewId);
        if (latestChosenViewId) {
          const chosenView = views.find((v) => String(v.id) === latestChosenViewId);
          if (chosenView && chosenView.data) {
            console.log('[useDataTableViews] Setting current view to latest chosen:', chosenView.name);
            setCurrentViewId(latestChosenViewId);
            // Don't call onApplyView here - let the page handle applying preferences
            return;
          } else {
            console.log('[useDataTableViews] Latest chosen view not found in loaded views');
          }
        }

        // Fall back to default view
        const defaultView = views.find((v) => v.data?.is_default);
        if (defaultView && defaultView.data) {
          console.log('[useDataTableViews] Setting current view to default:', defaultView.name);
          setCurrentViewId(String(defaultView.id));
        } else {
          console.log('[useDataTableViews] No default view found');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[useDataTableViews] Error loading views:', err);
          toast.error('Failed to load saved views');
        }
      }
    };

    loadViews();

    return () => {
      console.log('[useDataTableViews] Cleanup - cancelling load');
      cancelled = true;
    };
    // Only depend on entityType and normalizedTenantId - NOT onApplyView to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, normalizedTenantId]);

  const handleSelectView = (viewId: string) => {
    const view = savedViews.find((v) => String(v.id) === viewId);
    if (view && view.data) {
      listViewService.saveLatestChosenView(String(normalizedTenantId), viewId);
      setCurrentViewId(viewId);
      toast.success(`Loaded view: ${view.name}`);
    } else {
      toast.error('View not found');
    }
  };

  const handleSaveView = () => {
    if (!currentViewId) {
      toast.error('No view selected');
      return;
    }
    setSaveViewModalOpen(true);
  };

  const handleSaveAsView = () => {
    setSaveViewModalOpen(true);
  };

  const handleSaveViewModalSave = async (viewName: string, isPublic: boolean) => {
    try {
      if (currentViewId) {
        // Update existing view - in practice, this would need columnOrder, visibleColumns, etc.
        // For now, just show success
        toast.success('View updated successfully');
      } else {
        // Create new view
        const newView = await listViewService.saveListView(entityType, normalizedTenantId, {
          name: viewName,
          type: isPublic ? 'public' : 'private',
          data: {
            columnOrder: [],
            visibleColumns: [],
            columnWidths: {},
            sortBy: null,
            sortOrder: 'asc',
          },
        });
        const updatedViews = await listViewService.getListViews(entityType, normalizedTenantId);
        setSavedViews(updatedViews);
        setCurrentViewId(String(newView.id));
        toast.success('View saved successfully');
      }
      setSaveViewModalOpen(false);
    } catch (err: any) {
      console.error('Error saving view:', err);
      toast.error(err.message || 'Failed to save view');
    }
  };

  const handleDeleteView = async (viewId: string) => {
    try {
      await listViewService.deleteListView(Number(viewId), normalizedTenantId);
      const updatedViews = savedViews.filter((v) => String(v.id) !== String(viewId));
      setSavedViews(updatedViews);
      if (String(currentViewId) === String(viewId)) {
        setCurrentViewId(null);
        listViewService.clearLatestChosenView(String(normalizedTenantId));
      }
      toast.success('View deleted successfully');
    } catch (err: any) {
      console.error('Error deleting view:', err);
      toast.error(err.message || 'Failed to delete view');
    }
  };

  const handleSetDefaultView = async (viewId: string) => {
    try {
      await listViewService.setDefaultView(Number(viewId), normalizedTenantId);
      const updatedViews = await listViewService.getListViews(entityType, normalizedTenantId);
      setSavedViews(updatedViews);
      toast.success('Default view updated');
    } catch (err: any) {
      console.error('Error setting default view:', err);
      toast.error(err.message || 'Failed to set default view');
    }
  };

  const refreshViews = async () => {
    try {
      const updatedViews = await listViewService.getListViews(entityType, normalizedTenantId);
      setSavedViews(updatedViews);
    } catch (err) {
      console.error('Error refreshing views:', err);
    }
  };

  return {
    savedViews,
    currentViewId,
    saveViewModalOpen,
    setSaveViewModalOpen,
    setCurrentViewId,
    handleSelectView,
    handleSaveView,
    handleSaveAsView,
    handleDeleteView,
    handleSetDefaultView,
    handleSaveViewModalSave,
    refreshViews,
  };
}

export default useDataTableViews;
